/**
 * Sistema "live transform" per schemi Yup.
 *
 * Ogni schema può registrare due transform opzionali (salvate nei suoi `meta`):
 *
 * - **Context transform** (`.setLiveContextTransform(...)`): produce un context
 *   Yup arricchito a partire dai valori correnti del form. Tipico uso: esporre
 *   `$semanticFields.*`, `$VALIDATION_VALUES`, ecc. per `.when()`/`.test()`.
 *   Applicata automaticamente dal monkey-patch in `livePatch.ts` su
 *   `cast`/`validate`/`validateSync`/`resolve`.
 *
 * - **Values transform** (`.setLiveValuesTransform(...)`): riscrive i valori
 *   del form per mantenere invarianti cross-widget (es. allineare path che
 *   esportano lo stesso semantic tag univoco). NON è applicata dal patch —
 *   è il form layer (vedi `syncComputedValues`) che la invoca esplicitamente
 *   su onChange, perché richiede coordinazione con `_formValues` di RHF e
 *   l'evento puntuale `{ path, value }` che il patch non possiede.
 *
 * Questo file espone chiavi interne, marker di idempotenza, helper di lettura
 * e la registrazione di `.setLiveContextTransform` / `.setLiveValuesTransform`
 * sui costruttori yup. Gli applicatori vivono in
 * `applyLiveContextTransform.ts` e `applyLiveValuesTransform.ts`.
 */

import * as yup from 'yup';
import {
  type LiveContextTransform,
  type LiveValuesTransform,
} from './LiveTypes.ts';

// --- Chiavi interne ---

export const LIVE_CONTEXT_TRANSFORM_KEY = '__liveContextTransform';
export const LIVE_VALUES_TRANSFORM_KEY = '__liveValuesTransform';

/**
 * Meta key dei path RHF che alimentano il live context. Esportata perché
 * letta dall'UI (`useWatchReferences`) per sapere quali campi osservare.
 */
export const LIVE_FIELD_PATHS_META_KEY = 'livefieldspaths';

/** Path RHF che alimentano il live context (vuoto se non registrati). */
export function getLiveWatchedPaths(schema: yup.Schema): readonly string[] {
  return schema.meta()?.[LIVE_FIELD_PATHS_META_KEY] ?? [];
}

/**
 * Marker di idempotenza piazzato sul context arricchito.
 *
 * Symbol per non collidere con chiavi utente e per essere invisibile a
 * `JSON.stringify` / spread / deep-equal. `Symbol.for` (registry globale)
 * per sopravvivere a duplicazione del package in monorepo/HMR.
 */
export const HAS_LIVE_TRANSFORM_APPLIED = Symbol.for(
  'framework.hasLiveTransformApplied',
);

// --- Augmentation: setLiveContextTransform / setLiveValuesTransform ---
// cast/validate/validateSync/resolve sono invece monkey-patchati in
// `livePatch.ts`.

declare module 'yup' {
  interface Schema {
    /** Registra la `LiveContextTransform` sullo schema (vedi `livePatch.ts`). */
    setLiveContextTransform(transform: LiveContextTransform): this;
    /** Registra la `LiveValuesTransform` sullo schema (vedi `livePatch.ts`). */
    setLiveValuesTransform(transform: LiveValuesTransform): this;
  }
}

// --- Helper di lettura ---

/** True se lo schema ha registrato una `LiveContextTransform`. */
export function hasLiveContextTransform(schema: yup.Schema): boolean {
  return !!schema.meta()?.[LIVE_CONTEXT_TRANSFORM_KEY];
}

/** True se il context porta il marker di transform già applicata. */
export function hasLiveTransformApplied(context: any): boolean {
  return (
    context != null &&
    typeof context === 'object' &&
    context[HAS_LIVE_TRANSFORM_APPLIED] === true
  );
}

/**
 * Marca il context come già arricchito dal sistema live.
 *
 * `defineProperty` non-enumerable: il marker non deve propagarsi via spread
 * né influenzare deep-equal (la memoization del resolver ci si appoggia).
 */
export function markLiveTransformApplied(context: any): void {
  if (context == null || typeof context !== 'object') return;

  Object.defineProperty(context, HAS_LIVE_TRANSFORM_APPLIED, {
    value: true,
    enumerable: false,
    writable: false,
    configurable: false,
  });
}

// --- Registrazione di setLiveContextTransform / setLiveValuesTransform ---
// Yup non eredita `addMethod` dalla classe astratta `Schema`, va fatto per
// ogni costruttore.

/**
 * Registra i metodi `setLiveContextTransform` / `setLiveValuesTransform`
 * sul costruttore di schema fornito. Da chiamare per ogni tipo yup
 * (object, array, string, ecc.) perché `addMethod` non si propaga dalla
 * classe astratta `Schema`.
 */
function registerForSchema<T extends yup.Schema>(schema: (...arg: any[]) => T) {
  yup.addMethod<T>(
    schema,
    'setLiveContextTransform',
    function (this: T, transform: LiveContextTransform) {
      if (this.meta()?.[LIVE_CONTEXT_TRANSFORM_KEY]) {
        throw new Error(
          'setLiveContextTransform: schema ha già una LiveContextTransform registrata',
        );
      }
      return this.meta({ [LIVE_CONTEXT_TRANSFORM_KEY]: transform });
    },
  );

  yup.addMethod<T>(
    schema,
    'setLiveValuesTransform',
    function (this: T, transform: LiveValuesTransform) {
      if (this.meta()?.[LIVE_VALUES_TRANSFORM_KEY]) {
        throw new Error(
          'setLiveValuesTransform: schema ha già una LiveValuesTransform registrata',
        );
      }
      return this.meta({ [LIVE_VALUES_TRANSFORM_KEY]: transform });
    },
  );
}

registerForSchema(yup.object);
registerForSchema(yup.array);
